import { parsePlayerAuthErrorBody } from '@lucimark/shared/models/player-auth-error'

/** Server indicates stored tenant credentials are no longer valid. */
export const PLAYER_AUTH_REVOKE_REGISTRATION_CODES = new Set<string>([
  'invalid_secret',
  'player_not_found_in_tenant',
  'tenant_not_found',
  'player_id_mismatch',
  'invalid_player_id',
  'player_not_registered'
])

/** Auth failure that must not clear persisted registration (wrong resource / scope). */
export const PLAYER_AUTH_NEVER_CLEAR_REGISTRATION_CODES = new Set<string>([
  'not_authorized',
  'invalid_authentication'
])

/** Transient or dev race: retry once with a freshly minted bearer JWT before treating as failure. */
export const PLAYER_AUTH_SOFT_RETRY_CODES = new Set<string>([
  'invalid_bearer_token',
  'invalid_player_registration',
  'invalid_player_jwt'
])

const AMBIGUOUS_STREAK_WINDOW_MS = 5 * 60 * 1000
const AMBIGUOUS_STREAK_THRESHOLD = 5

/**
 * Minimum interval between two successful ambiguous-streak clears (ms).
 * After a clear fires, a second burst within this window is suppressed so a
 * network incident that triggers rapid reconnects cannot cascade into repeated
 * registration loss even when backup credentials restore successfully.
 */
const AMBIGUOUS_CLEAR_COOLDOWN_MS = 30 * 60 * 1000
let _lastAmbiguousClearAt: number | null = null

/** Isolated streaks so HTTP / WebSocket / pooling glitches do not stack toward one clear. */
export type TPlayerAuthAmbiguousChannel = 'http' | 'websocket' | 'pooling'

const ambiguousFailureTimestampsByChannel = new Map<TPlayerAuthAmbiguousChannel, number[]>()

export function resetPlayerAuthAmbiguousFailureStreak(): void {
  ambiguousFailureTimestampsByChannel.clear()
}

/**
 * Returns true and records the timestamp when an ambiguous clear is allowed to
 * proceed (outside the 30-minute cooldown window). Returns false when a recent
 * clear already fired and this burst should be suppressed.
 */
export function tryAcquireAmbiguousClearCooldown(): boolean {
  const now = Date.now()
  if (_lastAmbiguousClearAt !== null && now - _lastAmbiguousClearAt < AMBIGUOUS_CLEAR_COOLDOWN_MS) {
    return false
  }
  _lastAmbiguousClearAt = now
  return true
}

export function recordPlayerAuthAmbiguousFailure(channel: TPlayerAuthAmbiguousChannel): boolean {
  const now = Date.now()
  let timestamps = ambiguousFailureTimestampsByChannel.get(channel) ?? []
  timestamps = timestamps.filter(t => now - t < AMBIGUOUS_STREAK_WINDOW_MS)
  timestamps.push(now)
  ambiguousFailureTimestampsByChannel.set(channel, timestamps)
  return timestamps.length >= AMBIGUOUS_STREAK_THRESHOLD
}

export type TRegistrationAuthDecision = 'clear' | 'no_clear' | 'ambiguous'

/**
 * Whether to clear persisted `{ tenantId, secret }` after HTTP 401/403 on a player route.
 * Unknown or missing `code` requires consecutive failures on the same channel (see `recordPlayerAuthAmbiguousFailure`).
 */
export function shouldClearPersistedRegistration(opts: {
  code: string | undefined
  status: number
}): TRegistrationAuthDecision {
  const { code, status } = opts
  if (status === 503 && code === 'tenant_not_ready') {
    return 'no_clear'
  }
  if (status !== 401 && status !== 403) {
    return 'no_clear'
  }
  if (code != null && PLAYER_AUTH_NEVER_CLEAR_REGISTRATION_CODES.has(code)) {
    resetPlayerAuthAmbiguousFailureStreak()
    return 'no_clear'
  }
  if (code != null && PLAYER_AUTH_REVOKE_REGISTRATION_CODES.has(code)) {
    resetPlayerAuthAmbiguousFailureStreak()
    return 'clear'
  }
  if (code != null && PLAYER_AUTH_SOFT_RETRY_CODES.has(code)) {
    return 'no_clear'
  }
  return 'ambiguous'
}

export function parsePlayerAuthErrorFromResponseBody(data: unknown): string | undefined {
  return parsePlayerAuthErrorBody(data)?.code
}

/** Passed to `AuthenticationService.setRegistration` when clearing persisted registration. */
export type TRegistrationBackupDisposition = 'remove' | 'retain'

/**
 * Durable registration backup (`REGISTRATION_BACKUP_KEY`) is removed only after an explicit server
 * revoke. Ambiguous streak clears drop the primary copy only so Tauri/Android can still recover
 * from the backup file if the primary key was wiped selectively.
 */
export function registrationBackupDispositionForPersistedClear(
  reason: 'server_revoke' | 'ambiguous_streak'
): TRegistrationBackupDisposition {
  return reason === 'server_revoke' ? 'remove' : 'retain'
}
